import { useMemo, useState } from 'react'

import { Translation, useTranslatedText } from 'cng-common/features/translations'
import { Checkbox } from 'core-components/library/checkbox'
import { Input } from 'core-components/library/inputs'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { ICockpitSelectPartnersFilterProps } from '../../types'

import { CockpitSelectPartnersFilterRow } from './CockpitSelectPartnersFilterRow'

import styles from './CockpitSelectPartnersFilter.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.filterPanel

export const CockpitSelectPartnersFilter = ({
  partners,
  selectedPartnerIds,
  onChangeSelectedPartnerIds,
}: ICockpitSelectPartnersFilterProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [searchPlaceholder] = useTranslatedText(KEYS.searchPlaceholder)

  const filteredPartners = useMemo(
    () =>
      partners.filter((partner) =>
        partner.PartnerName.toLowerCase().includes(searchValue.trim().toLowerCase())
      ),
    [partners, searchValue]
  )
  const filteredIds = useMemo(
    () => filteredPartners.map((partner) => partner.PartnerId),
    [filteredPartners]
  )

  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedPartnerIds.includes(id))
  const someFilteredSelected = filteredIds.some((id) => selectedPartnerIds.includes(id))

  const handleToggleSelectAll = () => {
    onChangeSelectedPartnerIds(
      allFilteredSelected
        ? selectedPartnerIds.filter((id) => !filteredIds.includes(id))
        : Array.from(new Set([...selectedPartnerIds, ...filteredIds]))
    )
  }

  const toggleOne = (id: string) => {
    onChangeSelectedPartnerIds(
      selectedPartnerIds.includes(id)
        ? selectedPartnerIds.filter((existingId) => existingId !== id)
        : [...selectedPartnerIds, id]
    )
  }

  return (
    <div className={styles.root}>
      <Input
        classes={{ root: styles.searchInput }}
        value={searchValue}
        onChange={setSearchValue}
        startContent={<i className="kbase kbase-search" />}
        endContent={
          searchValue.length > 0 ? (
            <div className={styles.clearSearch} onClick={() => setSearchValue('')}>
              <i className="kbase kbase-close-circle" />
            </div>
          ) : undefined
        }
        placeholder={searchPlaceholder}
      />

      <Checkbox
        className={styles.selectAll}
        label={<Translation span text={KEYS.selectAll} />}
        checked={allFilteredSelected}
        indeterminate={someFilteredSelected && !allFilteredSelected}
        onChange={handleToggleSelectAll}
      />

      <div className={styles.divider} />

      <div className={styles.list}>
        {filteredPartners.map((partner) => (
          <CockpitSelectPartnersFilterRow
            key={partner.PartnerId}
            partner={partner}
            checked={selectedPartnerIds.includes(partner.PartnerId)}
            onToggle={toggleOne}
          />
        ))}
      </div>
    </div>
  )
}
